import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Backdrop,
  TablePagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Link,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import apis from "../../api";
import ConfirmWindow from "../../component/ConfirmWindow";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export const JobPage = () => {
  const userEmail = useSelector((state) => state.user.email);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Loading state for the page

  // Pagination state
  const [page, setPage] = useState(0);
  const rowsPerPage = 10; // Fixed to display 10 records per page

  const current = window.location.href.split("job")[0];
  const base_dir = current.includes("pennprs")
    ? current + "api/download?filename="
    : current.split("3000")[0] + "5000/api/download?filename=";

  const handleDownloadClick = (downloadLink) => {
    setIsLoading(true);
    window.location.href = downloadLink;
    setIsLoading(false);
  };

  const determineStatus = (job) => {
    if (job.status === "completed") {
      const downloadLink = `${base_dir}${userEmail}=${job.id}`;
      return (
        <div>
          <Typography
            variant="body2"
            style={{ color: "#4caf50", textAlign: "center", fontWeight: "bold" }}
          >
            {job.status}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => handleDownloadClick(downloadLink)}
            style={{ marginTop: "5px", textTransform: "none", fontSize: "14px" }}
          >
            Download Result
          </Button>
        </div>
      );
    } else if (job.status === "failed") {
      const downloadLink = `${base_dir}${userEmail}=${job.id}`;
      return (
        <div>
          <Typography
            variant="body2"
            style={{ color: "#e66b00", textAlign: "center", fontWeight: "bold" }}
          >
            {job.status}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => handleDownloadClick(downloadLink)}
            style={{ marginTop: "5px", textTransform: "none", fontSize: "14px" }}
          >
            Download Error Log
          </Button>
        </div>
      );
    } else if (job.status === "running") {
      return (
        <Typography
          variant="body2"
          style={{ color: "#1560bd", textAlign: "center", fontWeight: "bold" }}
        >
          {job.status}
        </Typography>
      );
    } else if (job.status.includes("queue")) {
      return (
        <Typography
          variant="body2"
          style={{ color: "lightgrey", textAlign: "center", fontWeight: "bold" }}
        >
          {job.status}
        </Typography>
      );
    } else if (job.status.includes("killed")) {
      return (
        <Typography
          variant="body2"
          style={{ color: "#e63600", textAlign: "center", fontWeight: "bold" }}
        >
          {job.status}
        </Typography>
      );
    }
    return job.status;
  };

  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [jobId, setJobId] = useState(null);

  // Job detail dialog state
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleJobClick = (job) => {
    setIsLoading(true);
    apis.getJobDetail({ email: userEmail, job_id: job.id }).then((res) => {
      setSelectedJob(res);
      setDetailOpen(true);
      setIsLoading(false);
    }).catch(() => {
      setSelectedJob(job);
      setDetailOpen(true);
      setIsLoading(false);
    });
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleConfirm = () => {
    setConfirm(true);
    setOpen(false);
  };

  const handleDelete = (jobId) => {
    setJobId(jobId);
    handleOpen();
  };

  useEffect(() => {
    if (confirm) {
      apis.deleteJob({ email: userEmail, job_id: jobId }).then((res) => {
        console.log(res);
        setConfirm(false);
        // Refresh the jobs list after deletion
        fetchJobs();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm, jobId]);

  const fetchJobs = () => {
    setIsLoading(true);
    apis.getJobs({ email: userEmail }).then((res) => {
      let temp = [];
      res.reverse();
      res.forEach((job) => {
        if (!job.hide) {
          temp.push(job);
        }
      });
      setJobs(temp);
      setIsLoading(false);
    }).catch((error) => {
      console.error("Error fetching jobs:", error);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const handleRefresh = () => {
    fetchJobs();
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Job summary counts for current user's jobs
  const jobSummary = {
    total: jobs.length,
    completed: jobs.filter((j) => j.status === "completed").length,
    failed: jobs.filter((j) => j.status === "failed").length,
    running: jobs.filter((j) => j.status === "running").length,
    queued: jobs.filter((j) => j.status && String(j.status).includes("queue")).length,
    killed: jobs.filter((j) => j.status && String(j.status).includes("killed")).length,
  };

  const statusColor = (status) => {
    if (status === "completed") return "#4caf50";
    if (status === "failed") return "#e66b00";
    if (status === "running") return "#1560bd";
    if (status && status.includes("queue")) return "#888";
    if (status && status.includes("killed")) return "#e63600";
    return "#333";
  };

  return (
    <Box mt={15}>
      <ConfirmWindow
        open={open}
        title=" ⚠️ Delete Job Confirmation"
        message="Are you sure you want to delete this job?"
        onConfirm={handleConfirm}
        onCancel={handleClose}
      />

      {/* Job Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={handleDetailClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {selectedJob && (
          <>
            <DialogTitle
              sx={{
                backgroundColor: "#002244",
                color: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pr: 1,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedJob.jobName || selectedJob.name || "Job Details"}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  ID: {selectedJob.id}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={selectedJob.status}
                  size="small"
                  sx={{
                    backgroundColor: statusColor(selectedJob.status),
                    color: "#fff",
                    fontWeight: "bold",
                    textTransform: "capitalize",
                  }}
                />
                <IconButton onClick={handleDetailClose} sx={{ color: "#fff" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
              {/* General Info */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                General Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Created At</Typography>
                  <Typography variant="body2">{selectedJob.created_at}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Job Type</Typography>
                  <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                    {selectedJob.jobType || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Ensemble</Typography>
                  <Typography variant="body2">{selectedJob.jobEnsemble ? "Yes" : "No"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Email Notification</Typography>
                  <Typography variant="body2">{selectedJob.jobEmailOpt ? "Enabled" : "Disabled"}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* PRS Methods */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                PRS Methods
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {(selectedJob.jobMethods || []).length > 0
                  ? (selectedJob.jobMethods).map((m) => (
                      <Chip key={m} label={m} variant="outlined" size="small" sx={{ borderColor: "#002244", color: "#002244" }} />
                    ))
                  : <Typography variant="body2" color="text.secondary">—</Typography>
                }
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Traits */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Trait(s)
              </Typography>
              {(selectedJob.traitsName || []).map((traitName, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{ p: 2, mb: 2, borderRadius: 1, borderColor: "#ddd" }}
                >
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Trait {idx + 1}: {traitName}
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Source</Typography>
                      <Typography variant="body2">{(selectedJob.traitsSource || [])[idx] || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Data Type</Typography>
                      <Typography variant="body2">{(selectedJob.traitsDetail || [])[idx] || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Trait Type</Typography>
                      <Typography variant="body2">{(selectedJob.traitsType || [])[idx] || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Population</Typography>
                      <Typography variant="body2">{(selectedJob.traitsPopoulation || [])[idx] || "—"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">GWAS ID</Typography>
                      <Typography variant="body2">
                        {(() => {
                          const idArr = selectedJob.trait_id;
                          const pathArr = selectedJob.trait_path;
                          const traitId = Array.isArray(idArr) ? idArr[idx] : idArr;
                          const traitPath = Array.isArray(pathArr) ? pathArr[idx] : pathArr;
                          const fallbackId =
                            (selectedJob.traitsCol || [])[idx]?.ID ||
                            (selectedJob.traitsCol || [])[idx]?.id;
                          const displayId = traitId || fallbackId || "—";
                          return traitId && traitPath ? (
                            <Link href={traitPath} target="_blank" rel="noopener noreferrer" underline="hover">
                              {displayId}
                            </Link>
                          ) : displayId;
                        })()}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Column / Sample size config */}
                  {(selectedJob.traitsCol || [])[idx] && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Sample Size / Column Config
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries((selectedJob.traitsCol)[idx])
                          .filter(([k]) => k !== "id" && k !== "ID")
                          .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                          .map(([k, v]) => (
                            <Grid item key={k}>
                              <Chip
                                label={`${k}: ${v}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "11px" }}
                              />
                            </Grid>
                          ))}
                        {Object.entries((selectedJob.traitsCol)[idx])
                          .filter(([k]) => k !== "id" && k !== "ID")
                          .every(([, v]) => v === "" || v === null || v === undefined) && (
                          <Grid item>
                            <Typography variant="body2" color="text.secondary">No column config specified</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Paper>
              ))}

              {(!selectedJob.traitsName || selectedJob.traitsName.length === 0) && (
                <Typography variant="body2" color="text.secondary">No trait information available.</Typography>
              )}

              {/* Algorithm Parameters */}
              {selectedJob.paraDict && Object.keys(selectedJob.paraDict).length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Algorithm Parameters
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(selectedJob.paraDict).map(([k, v]) => (
                      <Grid item key={k}>
                        <Tooltip title={`${k}: ${v}`} placement="top">
                          <Chip
                            label={`${k}: ${v}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "11px", maxWidth: 220 }}
                          />
                        </Tooltip>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {/* Download / Error log link if applicable */}
              {["completed", "failed"].includes(selectedJob.status) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Result
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      handleDetailClose();
                      handleDownloadClick(`${base_dir}${userEmail}=${selectedJob.id}`);
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    {selectedJob.status === "completed" ? "Download Result" : "Download Error Log"}
                  </Button>
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5 }}>
              <Button onClick={handleDetailClose} sx={{ textTransform: "none" }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 5 }}>
        <Typography variant="h4" mr={5} sx={{ fontWeight: 300 }}>
          Your Job Submissions
        </Typography>
        <Button
          onClick={handleRefresh}
          variant="outlined"
          sx={{
            color: "#002244",
            borderColor: "#002244",
            textTransform: "none",
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "#f1f8ff",
            },
          }}
        >
          <RefreshIcon sx={{ mr: 1 }} />
          Refresh
        </Button>
      </Box>

      {/* Job summary table */}
      <Paper
        elevation={0}
        sx={{
          padding: "2px",
          display: "flex",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <Table
          sx={{
            minWidth: 400,
            maxWidth: "90%",
            border: "1px solid #ddd",
            "& .MuiTableCell-head": {
              backgroundColor: "#002244",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "16px",
              textAlign: "center",
            },
            "& .MuiTableCell-body": {
              fontSize: "14px",
              padding: "12px 8px",
              textAlign: "center",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography component="span" fontWeight="bold">
                  Total Jobs
                </Typography>
              </TableCell>
              <TableCell>
                <Typography component="span" fontWeight="bold">
                  Completed
                </Typography>
              </TableCell>
              <TableCell>
                <Typography component="span" fontWeight="bold">
                  Failed
                </Typography>
              </TableCell>
              <TableCell>
                <Typography component="span" fontWeight="bold">
                  Running
                </Typography>
              </TableCell>
              <TableCell>
                <Typography component="span" fontWeight="bold">
                  Queued
                </Typography>
              </TableCell>
              <TableCell>
                <Typography component="span" fontWeight="bold">
                  Killed
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{jobSummary.total}</TableCell>
              <TableCell>{jobSummary.completed}</TableCell>
              <TableCell>{jobSummary.failed}</TableCell>
              <TableCell>{jobSummary.running}</TableCell>
              <TableCell>{jobSummary.queued}</TableCell>
              <TableCell>{jobSummary.killed}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      <Paper elevation={0} sx={{ padding: "2px", display: "flex", justifyContent: "center" }}>
        <Table
          sx={{
            minWidth: 700,
            maxWidth: "90%", // Limits width for better centering on large screens
            border: "1px solid #ddd",
            "& .MuiTableCell-head": {
              backgroundColor: "#002244",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "16px",
              textAlign: "center",
            },
            "& .MuiTableCell-body": {
              fontSize: "14px",
              padding: "12px 8px",
              textAlign: "center", // Centers all body cells
            },
            "& .MuiTableRow-root:hover": {
              backgroundColor: "#f1f8ff",
            },
            "& .MuiTableRow-root:nth-of-type(even)": {
              backgroundColor: "#f9f9f9",
            },
            margin: "0 auto", // Centers the table within the Paper container
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Job ID</TableCell>
              <TableCell>Job Name</TableCell>
              <TableCell>Job Status</TableCell>
              <TableCell>Method Type</TableCell>
              <TableCell>Creation Date</TableCell>
              <TableCell>Delete Result / Cancel Job</TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                colSpan={6}
                sx={{
                  backgroundColor: "#e8f0fe",
                  color: "#002244",
                  fontSize: "12px",
                  fontWeight: "normal",
                  padding: "4px 12px",
                  textAlign: "left",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: "middle" }} />
                Click on any row to view full job details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="h6">No job submissions found.</Typography>
                </TableCell>
              </TableRow>
            )}
            {jobs.length > 0 &&
              jobs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((job) => (
                  <TableRow
                    key={job.id}
                    onClick={() => handleJobClick(job)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{job.id}</TableCell>
                    <TableCell>{job.name}</TableCell>
                    <TableCell>{determineStatus(job)}</TableCell>
                    <TableCell>{job.type}-ancestry</TableCell>
                    <TableCell>{job.created_at}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {["completed", "failed", "killed"].includes(job.status) ? (
                        <Button
                          onClick={() => handleDelete(job.id)}
                          variant="outlined"
                          sx={{
                            color: "#ff5722",
                            borderColor: "#ff5722",
                            textTransform: "none",
                            fontSize: "14px",
                            "&:hover": {
                              backgroundColor: "#ffe0b2",
                            },
                          }}
                        >
                          <DeleteIcon /> Delete
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleDelete(job.id)}
                          variant="outlined"
                          sx={{
                            color: "#ff5722",
                            borderColor: "#ff5722",
                            textTransform: "none",
                            fontSize: "14px",
                            "&:hover": {
                              backgroundColor: "#ffe0b2",
                            },
                          }}
                        >
                          <CancelIcon /> Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Paper>
      {jobs.length > rowsPerPage && (
        <Stack spacing={2} sx={{ mt: 2, alignItems: "center" }}>
          <TablePagination
            component="div"
            count={jobs.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
          />
        </Stack>
      )}
    </Box>
  );
};
